import { Link } from 'react-router-dom';
import { ArrowUpRight, Linkedin, MapPin, Briefcase, Target, Sparkles, Compass, Leaf } from 'lucide-react';
import AINetwork from '@/components/AINetwork';
import CTABand from '@/components/CTABand';
import { FOUNDER } from '@/lib/content';

export default function AboutPage() {
  return (
    <div>
      <section className="relative pt-32 pb-16 lg:pt-44 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" aria-hidden />
        <AINetwork density={28} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-xs uppercase tracking-[0.22em] text-cyan-300/80 mb-5">About / Founder</div>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-semibold leading-[1.02] tracking-tighter max-w-4xl text-balance">
            Built by an operator.
            <span className="block gradient-text">For operators.</span>
          </h1>
          <p className="mt-7 max-w-2xl text-lg text-white/70 leading-relaxed">
            SKV Digital Labs was founded with a singular conviction: in the AI era,
            growth is an operating-system problem — not a marketing one.
          </p>
        </div>
      </section>

      {/* Founder Section */}
      <section className="relative py-20 lg:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-white/10 glow-cyan">
                <img
                  src="https://customer-assets.emergentagent.com/job_skv-ai-growth/artifacts/z66rpi7o_Sanjay%20Pro%20Pic.png"
                  alt="Sanjay K Varma — Founder, SKV Digital Labs"
                  className="absolute inset-0 w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#040914] via-transparent to-transparent" />
                <div className="absolute bottom-0 inset-x-0 p-7">
                  <div className="text-xs uppercase tracking-[0.22em] text-cyan-300 mb-2">Founder</div>
                  <div className="font-heading text-2xl font-semibold">{FOUNDER.name}</div>
                  <div className="text-sm text-white/70 mt-1">{FOUNDER.title}</div>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-3 gap-3">
                <div className="rounded-xl border border-white/10 p-4">
                  <Briefcase className="w-4 h-4 text-cyan-300 mb-2" />
                  <div className="font-heading text-xl font-semibold">15+</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-brand-ink">Years</div>
                </div>
                <div className="rounded-xl border border-white/10 p-4">
                  <MapPin className="w-4 h-4 text-cyan-300 mb-2" />
                  <div className="font-heading text-xl font-semibold">8+</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-brand-ink">Regions</div>
                </div>
                <div className="rounded-xl border border-white/10 p-4">
                  <Target className="w-4 h-4 text-cyan-300 mb-2" />
                  <div className="font-heading text-xl font-semibold">40+</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-brand-ink">Brands</div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7 lg:pt-8">
              <div className="text-xs uppercase tracking-[0.22em] text-cyan-300/80 mb-5">Profile</div>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mb-8">
                {FOUNDER.experience} engineering growth across global markets.
              </h2>

              <div className="space-y-5 text-base sm:text-lg text-white/75 leading-relaxed">
                {FOUNDER.bio.map((p, i) => (<p key={i}>{p}</p>))}
              </div>

              <div className="mt-10 rounded-2xl border-l-2 border-cyan-400 bg-cyan-400/[0.04] p-7">
                <div className="text-xs uppercase tracking-[0.22em] text-cyan-300 mb-3">Philosophy</div>
                <p className="font-heading text-xl sm:text-2xl leading-snug text-white/90 text-balance">
                  &ldquo;{FOUNDER.philosophy}&rdquo;
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/contact" className="btn-shimmer inline-flex items-center gap-2 px-6 py-3 rounded-full bg-cyan-400 text-[#040914] font-semibold hover:bg-cyan-300 transition-colors">
                  Schedule a Consultation <ArrowUpRight className="w-4 h-4" />
                </Link>
                <a href="https://www.linkedin.com/in/sanjay-varma/" target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 hover:border-cyan-400/40 transition-colors text-sm">
                  <Linkedin className="w-4 h-4" /> Connect on LinkedIn
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Practitioner Story */}
      <section data-testid="practitioner-story" className="relative py-20 lg:py-28 border-t border-white/5 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.22em] text-purple-300/80 mb-5">Practitioner & Believer</div>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight text-balance">
                I don't just market these practices.
                <span className="block gradient-text">I live them.</span>
              </h2>
            </div>

            <div className="lg:col-span-7 lg:pt-2">
              <div className="space-y-5 text-base sm:text-lg text-white/75 leading-relaxed">
                <p>
                  Alongside building SKV Digital Labs, I am a practising astrologer and Vastu
                  consultant. My interest in the occult, spiritual and healing sciences is not
                  professional convenience — it is a genuine, lifelong pursuit. I study charts,
                  advise on Vastu, and sit with the same questions of timing, energy and intention
                  that my clients devote their lives to.
                </p>
                <p>
                  This is what sets our work apart. When an astrologer, healer or Vastu consultant
                  comes to me, I already speak their language. I understand the sensitivity of this
                  world — the trust it demands, the authenticity it must protect, and the quiet
                  credibility that no amount of aggressive advertising can manufacture.
                </p>
                <p>
                  That understanding lets me translate a traditional practice into a modern,
                  international brand without diluting its soul. I have helped occult and wellness
                  practitioners reach global audiences, grow their consultation demand and increase
                  their revenue — while keeping the integrity of their craft entirely intact.
                </p>
              </div>

              <div className="mt-9 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { icon: Sparkles, t: 'Practising Astrologer', d: 'Hands-on study of charts, timing and remedies.' },
                  { icon: Compass, t: 'Vastu Consultant', d: 'Guided by tradition, applied with intent.' },
                  { icon: Leaf, t: 'Healing Advocate', d: 'A believer in holistic wellness and transformation.' },
                ].map((x, i) => {
                  const Ic = x.icon;
                  return (
                    <div key={i} className="rounded-2xl border border-purple-400/20 bg-purple-400/[0.04] p-5">
                      <Ic className="w-5 h-5 text-purple-300 mb-3" strokeWidth={1.6} />
                      <div className="font-heading text-base font-medium mb-1">{x.t}</div>
                      <div className="text-xs text-white/60 leading-relaxed">{x.d}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Philosophy */}
      <section className="relative py-24 lg:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {[
              { t: 'Strategy first, always', d: 'We start with the P&L, not the platform. Every engagement begins with growth math.' },
              { t: 'AI is the substrate', d: 'Models, agents and automation are designed into the operating fabric — not glued on top.' },
              { t: 'Data we can defend', d: 'Privacy-resilient measurement, MMM and incrementality — boardroom-grade evidence.' },
            ].map((x, i) => (
              <div key={i} className="rounded-2xl border border-white/10 bg-[#0B1221]/40 p-7">
                <div className="font-mono-display text-xs text-cyan-300/70 mb-4">PRINCIPLE 0{i + 1}</div>
                <h3 className="font-heading text-xl font-medium mb-3">{x.t}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{x.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABand title="Let's design your AI-native growth model." ctaLabel="Schedule a Consultation" />
    </div>
  );
}
